import { datadogLogs } from '@datadog/browser-logs';
import { BehaviorSubject } from 'rxjs';
import { v4 } from 'uuid';
import {
  DocParserHints,
  DocParserProcessingResponse,
  DocParserResponse,
  getParseStatusApi,
  getUploadUrlApi,
  parseDocumentApi,
  TaskCompletionStatus,
  uploadFileApi
} from '../Api/AI/aiApi';
import { Account } from '../types';
import { PotentialInventory } from '../components/Compounds/InvoiceParserForm/PotentialInventoryTable';

export interface InvoiceParserJob {
  id: string;
  fileNumber: number;
  fileSha: string;
  fileId: string;
  fileName: string;
  file: File | null;
  hints: DocParserHints;
  selectedAccount?: Account;
  taskCompletionStatus: TaskCompletionStatus;
  overallStatus: number;
  pollingStartTime?: number;
  statusInterval?: NodeJS.Timeout;
  status: 'completed' | 'error' | 'calculating_sha' | 'getting_upload_url' | 'uploading' | 'parsing' | 'idle';
  error?: string | null;
  pages: number;
  pagesRead: number;
  parseResult?: DocParserResponse | null;
  inventoryList?: PotentialInventory[];
  showAccountMismatchModal?: boolean;
}

export const defaultParserJob: InvoiceParserJob = {
  id: '',
  fileNumber: 0,
  fileSha: '',
  fileId: '',
  file: null,
  fileName: '',
  hints: {},
  taskCompletionStatus: {
    reading: { status: 0, pages: 0, pagesRead: 0 },
    docClassification: 0,
    parsing: 0,
    mapping: 0
  },
  overallStatus: 0,
  status: 'idle',
  error: null,
  pages: 0,
  pagesRead: 0,
  parseResult: null,
  inventoryList: [],
  showAccountMismatchModal: false
};

const defaultPotentialInventory: PotentialInventory[] = [];

const invoiceParserJobsSubject = new BehaviorSubject<Record<string, InvoiceParserJob>>({});
const potentialInventorySubject = new BehaviorSubject<PotentialInventory[]>(defaultPotentialInventory);
export const getInvoiceParserJobsSubject = (): BehaviorSubject<Record<string, InvoiceParserJob>> =>
  invoiceParserJobsSubject;
export const getPotentialInventorySubject = (): BehaviorSubject<PotentialInventory[]> => potentialInventorySubject;

export const getInvoiceParserJob = (id: string): InvoiceParserJob | null => {
  const jobs = invoiceParserJobsSubject.getValue();
  if (jobs && jobs[id]) {
    return jobs[id];
  }
  return null;
};

export const getNextInvoiceParserFileNumber = (): number => {
  const jobs = invoiceParserJobsSubject.getValue();
  if (!jobs) return 1;
  const fileNumbers = Object.values(jobs).map((job) => job.fileNumber);
  return fileNumbers.length > 0 ? Math.max(...fileNumbers) + 1 : 1;
};

export const createNewInvoiceParserJob = (id: string): InvoiceParserJob => {
  const jobs = invoiceParserJobsSubject.getValue() || {};
  if (jobs[id]) {
    return jobs[id];
  }
  jobs[id] = {
    ...defaultParserJob,
    id,
    fileNumber: getNextInvoiceParserFileNumber()
  };
  invoiceParserJobsSubject.next(jobs);
  return jobs[id];
};

export const updateInvoiceParserJob = (id: string, updates: Partial<InvoiceParserJob>): InvoiceParserJob | null => {
  const jobs = invoiceParserJobsSubject.getValue();
  if (jobs && jobs[id]) {
    jobs[id] = { ...jobs[id], ...updates };
    invoiceParserJobsSubject.next(jobs);
    return jobs[id];
  }
  return null;
};

export const removeInvoiceParserJob = (id: string): void => {
  const jobs = invoiceParserJobsSubject.getValue();
  if (jobs && jobs[id]) {
    if (jobs[id].statusInterval) clearInterval(jobs[id].statusInterval!);
    delete jobs[id];
    invoiceParserJobsSubject.next(jobs);
  }
};

export const clearInvoiceParserJobs = (): void => {
  Object.values(invoiceParserJobsSubject.getValue() || {}).forEach((job) => {
    if (job.statusInterval) clearInterval(job.statusInterval);
  });
  invoiceParserJobsSubject.next({});
};

export const handleStartNewInvoiceParserJob = async (jobId: string, docId: string, contentType: string, file: File) => {
  const job = getInvoiceParserJob(jobId);
  if (!job) return;
  job.status = 'getting_upload_url';
  job.fileId = docId;
  updateInvoiceParserJob(jobId, job);
  const uploadUrlResponse = await getUploadUrlApi(docId, contentType);
  if (!uploadUrlResponse.error) {
    // Upload file only if URL is provided
    if (uploadUrlResponse.data.url) {
      job.status = 'uploading';
      updateInvoiceParserJob(jobId, job);
      await uploadFileApi(uploadUrlResponse.data.url, file);
    }

    // Initiate parsing and handle different response types
    job.status = 'parsing';
    updateInvoiceParserJob(jobId, job);
    try {
      const hints: DocParserHints = job.selectedAccount ? { customerId: job.selectedAccount.id } : {};
      job.hints = hints;
      updateInvoiceParserJob(jobId, job);
      const response = await parseDocumentApi(docId, 'outside_inventory', hints);
      if (response.error || (response?.data as DocParserProcessingResponse)?.status === 'error') {
        datadogLogs.logger.error('Error during parsing:', { error: response.error });
        job.status = 'error';
        job.error = `Failed to process file: ${response.error?.message || (response?.data as DocParserProcessingResponse)?.message || 'Unknown error'}`;
        updateInvoiceParserJob(jobId, job);
        return;
      }
      // Use proper type checking for different response types
      if ('status' in response.data && (response.data.status === 'in_progress' || response.data.status === 'started')) {
        // Document is being processed, start polling
        startInvoiceParserJobPolling(jobId);
      } else {
        // Document was already processed, response is DocParserResponse
        job.taskCompletionStatus = {
          reading: { status: 2, pages: job.pages, pagesRead: job.pagesRead },
          docClassification: 2,
          parsing: 2,
          mapping: 2
        };
        // Set customer information for all inventory items
        const { result: modifiedResult, hasAccountMismatch } = setCustomerInfoOnInventoryItems(
          response.data as DocParserResponse,
          job.selectedAccount
        );
        job.parseResult = modifiedResult;
        job.inventoryList = (JSON.parse(modifiedResult.document.json).inventoryItems || []).map(
          (item: PotentialInventory, idx: number) => ({
            ...item,
            fileNumber: `${job.fileNumber}.${idx + 1}`
          })
        );
        if (hasAccountMismatch) {
          job.showAccountMismatchModal = true;
        }
        job.status = 'completed';
        updateInvoiceParserJob(jobId, job);
      }
    } catch (err) {
      datadogLogs.logger.error(
        'Error during parsing:',
        err instanceof Error ? { message: err.message, stack: err.stack } : { error: err }
      );
      job.status = 'error';
      job.error = `Failed to process file: ${err}`;
      updateInvoiceParserJob(jobId, job);
    }
  } else {
    datadogLogs.logger.error('Error processing file:', { error: uploadUrlResponse.error });
    job.error = 'Failed to process file';
    job.status = 'error';
    updateInvoiceParserJob(jobId, job);
  }
};

export const startInvoiceParserJobPolling = (jobId?: string): void => {
  // Placeholder for starting polling logic
  if (!jobId) return;
  const job = getInvoiceParserJob(jobId);
  if (!job) return;

  const { statusInterval } = job;
  if (statusInterval) {
    clearInterval(statusInterval);
    job.statusInterval = undefined;
  }
  job.pollingStartTime = Date.now();
  updateInvoiceParserJob(jobId, job);
  const intervalId = setInterval(async () => {
    const job = getInvoiceParserJob(jobId);
    if (!job) return;

    const { pollingStartTime, hints, selectedAccount } = job;
    try {
      // Check if polling has been running for more than 60 minutes
      if (pollingStartTime && Date.now() - pollingStartTime > 60 * 60 * 1000) {
        clearInterval(intervalId);
        job.statusInterval = undefined;
        job.error = 'Polling timed out after 60 minutes';
        job.status = 'error';
        updateInvoiceParserJob(jobId, job);
        return;
      }
      const statusResponse = await getParseStatusApi(job.fileId, 'outside_inventory', hints);
      if (statusResponse.error) {
        job.error = `Failed to get parsing status: ${statusResponse.error.message}`;
        job.status = 'error';
        clearInterval(intervalId);
        job.statusInterval = undefined;
        job.pollingStartTime = undefined;
        updateInvoiceParserJob(jobId, job);
        return;
      }

      // Find overall progress status
      const overallStatus = statusResponse.data.tasks?.find((t) => t.task === 'overall-progress');
      const ocrPages = statusResponse.data.tasks.filter((t) => t.task.includes('ocr'));
      const readOcrPages = ocrPages.filter((t) => 'elapsed_time' in t && t.elapsed_time && t.elapsed_time > 0);
      job.pages = ocrPages.length || 1;
      job.pagesRead = readOcrPages.length;
      job.taskCompletionStatus = {
        ...job.taskCompletionStatus,
        reading: {
          ...job.taskCompletionStatus.reading,
          pages: job.pages,
          pagesRead: job.pagesRead
        }
      };
      updateInvoiceParserJob(jobId, job);
      if (overallStatus) {
        if (overallStatus.status === 'complete') {
          // Processing complete, get the result by calling parseDocument again
          const result = await parseDocumentApi(job.fileId, 'outside_inventory', hints);
          if (result.error || (result?.data as DocParserProcessingResponse)?.status === 'error') {
            job.status = 'error';
            job.error = `Processing failed: ${result.error?.message || (result?.data as DocParserProcessingResponse)?.message || 'Unknown error'}`;
            clearInterval(intervalId);
            job.statusInterval = undefined;
            updateInvoiceParserJob(jobId, job);
            return;
          }
          // Type check to ensure we're only setting DocParserResponse
          if (result?.data && 'document' in result.data) {
            // Set customer information for all inventory items
            const { result: modifiedResult, hasAccountMismatch } = setCustomerInfoOnInventoryItems(
              result.data,
              selectedAccount
            );
            job.parseResult = modifiedResult;
            job.inventoryList = (JSON.parse(modifiedResult.document.json).inventoryItems || []).map(
              (item: PotentialInventory, idx: number) => ({
                ...item,
                fileNumber: `${job.fileNumber}.${idx + 1}`
              })
            );
            if (hasAccountMismatch) {
              job.showAccountMismatchModal = true;
            }
            job.taskCompletionStatus = {
              reading: { status: 2, pages: job.pages, pagesRead: job.pagesRead },
              docClassification: 2,
              parsing: 2,
              mapping: 2
            };
            job.status = 'completed';
            clearInterval(intervalId);
            job.statusInterval = undefined;
            updateInvoiceParserJob(jobId, job);
          }
        } else if (overallStatus.status === 'error') {
          job.error = `Processing failed: ${overallStatus.error || 'Unknown error'}`;
          job.taskCompletionStatus = (() => {
            const { reading, docClassification, parsing, mapping } = job.taskCompletionStatus;
            return {
              reading: { ...reading, status: reading.status === 1 ? 3 : reading.status },
              docClassification: docClassification === 1 ? 3 : docClassification,
              parsing: parsing === 1 ? 3 : parsing,
              mapping: mapping === 1 ? 3 : mapping
            };
          })();
          job.status = 'error';
          clearInterval(intervalId);
          job.statusInterval = undefined;
          job.pollingStartTime = undefined;
          updateInvoiceParserJob(jobId, job);
        } else if (overallStatus.status === 'processing_started') {
          if (statusResponse.data.tasks.every((t) => t.task === 'overall-progress' || t.task.includes('ocr'))) {
            job.taskCompletionStatus = {
              ...job.taskCompletionStatus,
              reading: {
                ...job.taskCompletionStatus.reading,
                status: ocrPages.length === readOcrPages.length ? 2 : 1
              }
            };
            updateInvoiceParserJob(jobId, job);
          } else if (
            statusResponse.data.tasks.some((t) => t.task === 'llm-classify-doc') &&
            !statusResponse.data.tasks.some((t) => t.task === 'llm-parse-json')
          ) {
            const classifyTask = statusResponse.data.tasks.find((t) => t.task === 'llm-classify-doc');
            job.taskCompletionStatus = {
              ...job.taskCompletionStatus,
              docClassification:
                classifyTask &&
                'elapsed_time' in classifyTask &&
                classifyTask.elapsed_time &&
                classifyTask.elapsed_time > 0
                  ? 2
                  : 1,
              reading: {
                ...job.taskCompletionStatus.reading,
                status: 2
              }
            };
            updateInvoiceParserJob(jobId, job);
          } else if (statusResponse.data.tasks.some((t) => t.task === 'llm-parse-json')) {
            const parseTask = statusResponse.data.tasks.find((t) => t.task === 'llm-parse-json');
            job.taskCompletionStatus = {
              ...job.taskCompletionStatus,
              parsing:
                parseTask && 'elapsed_time' in parseTask && parseTask.elapsed_time && parseTask.elapsed_time > 0
                  ? 2
                  : 1,
              docClassification: 2,
              reading: {
                ...job.taskCompletionStatus.reading,
                status: 2
              }
            };
            updateInvoiceParserJob(jobId, job);
          }
        }
      }
    } catch (err) {
      datadogLogs.logger.error(
        'Error polling status:',
        err instanceof Error ? { message: err.message, stack: err.stack } : { error: err }
      );
      clearInterval(intervalId);
      job.statusInterval = undefined;
      job.error = 'Failed to get parsing status';
      job.status = 'error';
      updateInvoiceParserJob(jobId, job);
    }
  }, 5000); // Poll every 5 seconds
  job.statusInterval = intervalId;
  updateInvoiceParserJob(jobId, job);
};

export const retryInvoiceParserJob = (jobId: string): void => {
  const job = getInvoiceParserJob(jobId);
  if (!job) return;
  if (job.statusInterval) {
    clearInterval(job.statusInterval);
    job.statusInterval = undefined;
  }
  job.status = 'idle';
  job.error = null;
  job.taskCompletionStatus = {
    reading: { status: 0, pages: 0, pagesRead: 0 },
    docClassification: 0,
    parsing: 0,
    mapping: 0
  };
  job.parseResult = null;
  job.pages = 0;
  job.pagesRead = 0;
  job.inventoryList = [];
  updateInvoiceParserJob(jobId, job);
  handleStartNewInvoiceParserJob(jobId, job.fileId, job.file ? job.file.type : 'application/octet-stream', job.file!);
};

const setCustomerInfoOnInventoryItems = (
  result: DocParserResponse,
  selectedAccount: Account | undefined
): { result: DocParserResponse; hasAccountMismatch: boolean } => {
  if (!selectedAccount || !result.document?.json) {
    return {
      result: {
        ...result,
        document: {
          ...result.document,
          json: JSON.stringify({})
        }
      },
      hasAccountMismatch: false
    };
  }
  try {
    // Parse the JSON string to modify the inventory items
    const parsedData = JSON.parse(result.document.json);
    let hasAccountMismatch = false;

    if (parsedData.inventoryItems && Array.isArray(parsedData.inventoryItems)) {
      // Process each inventory item
      parsedData.inventoryItems = parsedData.inventoryItems.map((item: Record<string, unknown>) => {
        item.tempId = v4();
        const hasExistingCustomerName =
          item.customerName && typeof item.customerName === 'string' && item.customerName.trim() !== '';
        const hasExistingCustomerId =
          item.customerId && typeof item.customerId === 'string' && item.customerId.trim() !== '';

        if (item.extraCharges && Array.isArray(item.extraCharges)) {
          item.extraCharges = item.extraCharges.map((charge: Record<string, unknown>, idx: number) => ({
            id: `${item.externalId || 'unknown'}-${charge.name || 'charge'}`,
            fileNumber: `${item.fileNumber || 0}.${idx + 1}`,
            externalId: charge.name || 'Unknown Charge',
            parentTempId: item.tempId,
            mRC: charge.value || 0,
            mRC_confidence: charge.value_confidence || 0,
            mRC_explanation: charge.value_explanation || null,
            mRCWithoutCharges: charge.value || 0,
            mRCWithoutCharges_confidence: charge.value_confidence || 0,
            mRCWithoutCharges_explanation: charge.value_explanation || null,
            nRC: 0,
            nRC_confidence: 1,
            nRC_explanation: null,
            customerName: item.customerName,
            customerId: item.customerId,
            supplierName: item.supplierName,
            supplierId: item.supplierId,
            productId: item.productId,
            productName: item.productName,
            ...charge
          }));
        }

        // Check if existing customer info doesn't match selected account
        if (hasExistingCustomerName || hasExistingCustomerId) {
          if (item.customerName !== selectedAccount.name || item.customerId !== selectedAccount.id) {
            hasAccountMismatch = true;
          }
          // Keep existing customer info if it exists
          return item;
        } else {
          // Set customer info only if it doesn't exist
          return {
            ...item,
            customerName: selectedAccount.name,
            customerId: selectedAccount.id,
            extraCharges: ((item.extraCharges as Record<string, string | number>[]) || []).map((charge) => ({
              ...charge,
              customerName: selectedAccount.name,
              customerId: selectedAccount.id
            }))
          };
        }
      });
    } else {
      parsedData.inventoryItems = [];
    }
    // Update the JSON string with the modified data
    const updatedResult = {
      ...result,
      document: {
        ...result.document,
        json: JSON.stringify(parsedData),
        customerId: selectedAccount.id,
        customerName: selectedAccount.name
      }
    };

    return { result: updatedResult, hasAccountMismatch };
  } catch (error) {
    datadogLogs.logger.warn(
      'Error parsing JSON while setting customer info:',
      error instanceof Error ? { message: error.message, stack: error.stack } : { error }
    );
  }

  return { result, hasAccountMismatch: false };
};
